import { ref, computed } from 'vue';
import { supabase } from '@/services/supabase';
import { ProcessStatus } from '@/types/processStatus';
import { TemplateStatus } from '@/types/supabase';
import { DOCUMENT_STATUS } from '@/types/document';
import { logger, LogCategory } from '@/services/logger';
import type {
  ForecastingProcess,
  ForecastingProcessInsert,
  ForecastingProcessUpdate,
  Template,
  TemplateInsert,
  TemplateUpdate
} from '@/types/supabase';

export function useTemplates() {
  // Use any type to avoid TypeScript errors
  const templates = ref<any[]>([]);
  const selectedTemplate = ref<Template | null>(null);
  const loading = ref(false);
  const error = ref<Error | null>(null);

  // Helper functions to get templates by status
  function getPendingTemplates(): Template[] {
    const result: Template[] = [];
    for (const t of templates.value) {
      if (t.status === 'pending') {
        result.push(t);
      }
    }
    return result;
  }

  function getApprovedTemplates(): Template[] {
    const result: Template[] = [];
    for (const t of templates.value) {
      if (t.status === 'approved') {
        result.push(t);
      }
    }
    return result;
  }

  function getRejectedTemplates(): Template[] {
    const result: Template[] = [];
    for (const t of templates.value) {
      if (t.status === 'rejected') {
        result.push(t);
      }
    }
    return result;
  }

  // Computed properties that use the helper functions
  const pendingTemplates = computed(() => getPendingTemplates());
  const approvedTemplates = computed(() => getApprovedTemplates());
  const rejectedTemplates = computed(() => getRejectedTemplates());

  /**
   * Fetch all templates
   */
  async function fetchTemplates() {
    loading.value = true;

    try {
      const { data, error: err } = await supabase
        .from('templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (err) throw err;

      templates.value = data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch a single template by ID
   */
  async function fetchTemplateById(id: string) {
    loading.value = true;

    try {
      const { data, error: err } = await supabase
        .from('templates')
        .select('*')
        .eq('id', id)
        .single();

      if (err) throw err;

      selectedTemplate.value = data;
      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Create a new template
   */
  async function createTemplate(template: TemplateInsert) {
    loading.value = true;

    try {
      const { data, error: err } = await supabase
        .from('templates')
        .insert({
          ...template,
          created_at: new Date().toISOString(),
          status: 'pending',
        })
        .select()
        .single();

      if (err) throw err;

      // Add to local state
      // Add to local state without using spread operator
      const newTemplates: Template[] = [data];
      for (const t of templates.value) {
        newTemplates.push(t);
      }
      templates.value = newTemplates;

      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update an existing template
   */
  async function updateTemplate(id: string, updates: TemplateUpdate) {
    loading.value = true;

    try {
      const { data, error: err } = await supabase
        .from('templates')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (err) throw err;

      // Update in local state
      const index = templates.value.findIndex(t => t.id === id);
      if (index !== -1) {
        templates.value[index] = data;
      }

      if (selectedTemplate.value?.id === id) {
        selectedTemplate.value = data;
      }

      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Delete a template
   */
  async function deleteTemplate(id: string) {
    loading.value = true;

    try {
      const { error: err } = await supabase
        .from('templates')
        .delete()
        .eq('id', id);

      if (err) throw err;

      // Remove from local state
      // Remove from local state without using filter
      const newTemplates: Template[] = [];
      for (const t of templates.value) {
        if (t.id !== id) {
          newTemplates.push(t);
        }
      }
      templates.value = newTemplates;

      if (selectedTemplate.value?.id === id) {
        selectedTemplate.value = null;
      }

      return true;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update template status
   */
  async function updateTemplateStatus(id: string, status: TemplateStatus) {
    // Convert TemplateStatus enum (uppercase) to lowercase string for Template type
    const statusMap: Record<TemplateStatus, 'pending' | 'approved' | 'rejected'> = {
      [TemplateStatus.PENDING]: 'pending',
      [TemplateStatus.APPROVED]: 'approved',
      [TemplateStatus.REJECTED]: 'rejected'
    };

    return updateTemplate(id, { status: statusMap[status] });
  }

  /**
   * Subscribe to real-time updates for templates
   */
  function subscribeToTemplates(callback: (payload: any) => void) {
    return supabase
      .channel('templates-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'templates' },
        callback
      )
      .subscribe();
  }

  return {
    templates,
    selectedTemplate,
    loading,
    error,
    pendingTemplates,
    approvedTemplates,
    rejectedTemplates,
    getPendingTemplates,
    getApprovedTemplates,
    getRejectedTemplates,
    fetchTemplates,
    fetchTemplateById,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    updateTemplateStatus,
    subscribeToTemplates
  };
}


export function useDocuments() {
  const documents = ref<any[]>([]);
  const selectedDocument = ref<any>(null);
  const comments = ref<any[]>([]);
  const loading = ref(false);
  const error = ref<Error | null>(null);

  /**
   * Helper function to extract fileId from a URL string
   * @param url URL string that may contain a fileId
   * @returns The extracted fileId (filename after the last '/' character)
   */
  function extractFileIdFromUrl(url: string): string {
    if (!url) return '';

    // Extract the part after the last '/' character
    const parts = url.split('/');
    return parts[parts.length - 1];
  }

  /**
   * Fetch documents from the documents table
   */
  async function fetchCurrentDocuments() {
    loading.value = true;

    try {
      const { data, error: err } = await supabase
        .from('documents')  // Änderung von 'document_profile' zu 'documents'
        .select('*')

      if (err) throw err;

      documents.value = data;
      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      return [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch documents from the document_profile view filtered by forecast_id
   * @param forecastId The ID of the forecasting process to filter by
   */
  async function fetchDocumentsByForecastId(forecastId: string) {
    loading.value = true;

    try {
      logger.info('Fetching documents by forecast ID', LogCategory.DATABASE, { forecastId });
      
      // First, fetch documents from the documents table
      const { data, error: err } = await supabase
        .from('documents')
        .select('*')
        .eq('forecast_id', forecastId);

      if (err) throw err;

      // If we have documents, enhance them with owner information
      if (data && data.length > 0) {
        // Get unique creator IDs
        const creatorIds = [...new Set(data.map(doc => doc.created_by))].filter(Boolean);
        
        if (creatorIds.length > 0) {
          // Fetch profiles for all creators in one query
          const { data: profiles, error: profilesErr } = await supabase
            .from('profiles')
            .select('id, name, avatar_url, email')
            .in('id', creatorIds);
          
          if (!profilesErr && profiles) {
            // Create a map of profiles by ID for quick lookup
            const profileMap = profiles.reduce((map, profile) => {
              map[profile.id] = profile;
              return map;
            }, {});
            
            // Enhance each document with owner information
            data.forEach(doc => {
              const profile = profileMap[doc.created_by];
              if (profile) {
                doc.created_by_name = profile.name || doc.created_by || 'Unknown User';
                doc.created_by_user_id = doc.created_by;
                doc.created_by_avatar_url = profile.avatar_url || null;
                doc.planner_email = profile.email || '';
              } else {
                doc.created_by_name = doc.created_by || 'Unknown User';
                doc.created_by_user_id = doc.created_by;
                doc.created_by_avatar_url = null;
                doc.planner_email = '';
              }
              
              // Map process fields
              doc.process_id = doc.forecast_id;
            });
          }
        }
      }

      documents.value = data || [];
      logger.info('Documents fetched successfully', LogCategory.DATABASE, {
        forecastId,
        count: data?.length || 0
      });
      
      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      logger.error('Error fetching documents by forecast ID', LogCategory.DATABASE, {
        forecastId,
        error: err
      });
      
      return [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch comments for a document
   */
  async function fetchComments(referenceId: string) {
    loading.value = true;

    try {
      const { data, error: err } = await supabase
        .from('comments_profiles')
        .select('id, created_at, created_by, content, reference_id, created_by_name, created_by_avatar_url')
        .eq('reference_id', referenceId)
        .order('created_at', { ascending: false });

      if (err) throw err;

      comments.value = data;
      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      return [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Insert a new comment
   */
  async function insertComment(referenceId: string, content: string) {
    loading.value = true;

    const { data: sessionData } = await supabase.auth.getSession()

    try {
      const { data, error: err } = await supabase
        .from('comments')
        .insert({
          reference_id: referenceId,
          content: content,
          created_by: sessionData.session?.user.id
        })
        .select()
        .single();

      if (err) throw err;

      // Refresh comments after inserting
      await fetchComments(referenceId);
      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Select a document for viewing details
   */
  async function selectDocument(doc: any) {
    selectedDocument.value = doc;
    if (doc) {
      await fetchComments(doc.id);
    } else {
      comments.value = [];
    }
  }

  /**
   * Clear the selected document
   */
  function clearSelectedDocument() {
    selectedDocument.value = null;
    comments.value = [];
  }

  /**
   * Update a document's status in the database
   * @param documentId The document ID
   * @param newStatus The new status to set
   * @returns True if the update was successful, false otherwise
   */
  async function updateDocumentStatus(documentId: string, newStatus: DOCUMENT_STATUS): Promise<boolean> {
    try {
      logger.info('Updating document status', LogCategory.DATABASE, { documentId, newStatus });

      // Get current user ID
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;

      if (!userId) {
        logger.warn('No user ID available for document status update', LogCategory.DATABASE, { documentId, newStatus });
      }

      // Update the document status in the database with updated_by and updated_at
      const { error: updateError } = await supabase
        .from('documents')
        .update({
          status: newStatus,
          updated_by: userId || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', documentId);

      if (updateError) {
        logger.error('Error updating document status', LogCategory.DATABASE, { documentId, newStatus, error: updateError });
        return false;
      }

      logger.info('Document status updated successfully', LogCategory.DATABASE, {
        documentId,
        newStatus,
        updated_by: userId,
        updated_at: new Date().toISOString()
      });
      return true;
    } catch (err) {
      logger.error('Exception in updateDocumentStatus', LogCategory.DATABASE, { documentId, newStatus, error: err });
      return false;
    }
  }

  /**
   * Download a document by its ID
   */
  async function downloadDocument(documentId: string) {
    if (!documentId) {
      console.error('No document ID provided for download');
      return;
    }

    try {
      // First, fetch the document to get the file_id
      const { data: documents, error: docsErr } = await supabase
        .from('documents')
        .select('id, file_id')
        .eq('id', documentId);

      if (docsErr) {
        console.error('Error fetching document:', docsErr);
        return;
      }

      if (!documents || documents.length === 0) {
        console.error('No document found with the specified ID');
        return;
      }

      const fileId = documents[0].file_id;

      if (!fileId) {
        console.error('Document has no file_id');
        return;
      }

      // Then, fetch the file to get the URL
      const { data: files, error: filesErr } = await supabase
        .from('files')
        .select('id, name, type, url')
        .eq('id', fileId);

      if (filesErr) {
        console.error('Error fetching file:', filesErr);
        return;
      }

      if (!files || files.length === 0) {
        console.error('No file found with the specified file_id');
        return;
      }

      const fileUrl = files[0].url;
      const extractedFileIdFromUrl = extractFileIdFromUrl(fileUrl);

      // Construct the URL for the Fission Function
      const fissionUrl = (process.env.VUE_APP_FISSION_URL || 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/') + `template-download?templateId=${extractedFileIdFromUrl}`;

      logger.info('Template download URL', LogCategory.STORAGE, { fissionUrl });

      // Create a download link and trigger the download
      const link = document.createElement('a');
      link.href = fissionUrl;
      link.download = files[0].name || 'document'; // Set download attribute to force download instead of navigation
      link.rel = 'noopener noreferrer'; // Security best practice

      // Add to the document and click
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);

      logger.info('Document download initiated', LogCategory.STORAGE, { documentId });

    // Removed the functionality to update the document status to 'downloaded' in the database
    // Documents can be downloaded multiple times (e.g. by the Head of PCO), so we don't need to track this in the database
    
      return fileUrl;
    } catch (err) {
      console.error('Error in downloadDocument:', err);
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    }
  }

  return {
    documents,
    selectedDocument,
    comments,
    loading,
    error,
    fetchCurrentDocuments,
    fetchDocumentsByForecastId,
    fetchComments,
    insertComment,
    selectDocument,
    clearSelectedDocument,
    updateDocumentStatus,
    downloadDocument
  };
}

export function useForecastingProcess() {
  // Use any type to avoid TypeScript errors
  const processes = ref<any[]>([]);
  const currentProcess = ref<ForecastingProcess | null>(null);
  const loading = ref(false);
  const error = ref<Error | null>(null);

  // Helper function to get active processes
  function getActiveProcesses(): ForecastingProcess[] {
    const result: ForecastingProcess[] = [];
    for (const p of processes.value) {
      if (p.status === ProcessStatus.CREATED) {
        result.push(p);
      }
    }
    return result;
  }

  // Computed property that uses the helper function
  const activeProcesses = computed(() => getActiveProcesses());

  /**
   * Fetch all forecasting processes
   */
  async function fetchProcesses() {
    // Set a timeout to reset loading state if it gets stuck
    const loadingTimeout = setTimeout(() => {
      if (loading.value === true) {
        logger.warn('Loading state stuck in fetchProcesses, resetting', LogCategory.DATABASE);
        loading.value = false;
      }
    }, 10000); // 10 second timeout
    
    loading.value = true;

    try {
      const { data, error: err } = await supabase
        .from('forecasting_process')
        .select('*')
        .order('created_at', { ascending: false });

      if (err) throw err;

      processes.value = data;
      
      logger.info('Processes fetched successfully', LogCategory.DATABASE, {
        count: data?.length || 0
      });
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      logger.error('Error fetching processes', LogCategory.DATABASE, { error: err });
    } finally {
      loading.value = false;
      clearTimeout(loadingTimeout);
    }
  }

  /**
   * Fetch a single forecasting process by ID
   */
  async function fetchProcessById(id: string) {
    loading.value = true;

    try {
      const { data, error: err } = await supabase
        .from('forecasting_process')
        .select('*')
        .eq('id', id)
        .single();

      if (err) throw err;

      currentProcess.value = data;
      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Create a new forecasting process
   */
  async function createProcess(process: ForecastingProcessInsert) {
    loading.value = true;

    try {
      const { data, error: err } = await supabase
        .from('forecasting_process')
        .insert({
          ...process,
          current_step: 1, // Start at step 1
          created_at: new Date().toISOString(),
          status: ProcessStatus.CREATED,
        })
        .select()
        .single();

      if (err) throw err;

      // Add to local state
      // Add to local state without using spread operator
      const newProcesses: ForecastingProcess[] = [data];
      for (const p of processes.value) {
        newProcesses.push(p);
      }
      processes.value = newProcesses;

      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update an existing forecasting process
   */
  async function updateProcess(id: string, updates: ForecastingProcessUpdate) {
    loading.value = true;

    try {
      const { data, error: err } = await supabase
        .from('forecasting_process')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (err) throw err;

      // Update in local state
      const index = processes.value.findIndex(p => p.id === id);
      if (index !== -1) {
        processes.value[index] = data;
      }

      if (currentProcess.value?.id === id) {
        currentProcess.value = data;
      }

      return data;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update process step
   */
  async function updateProcessStep(id: string, step: number) {
    return updateProcess(id, { current_step: step });
  }

  /**
   * Complete a forecasting process
   */
  async function completeProcess(id: string) {
    return updateProcess(id, {
      status: ProcessStatus.COMPLETED,
      end_date: new Date().toISOString()
    });
  }

  /**
   * Cancel a forecasting process
   */
  async function cancelProcess(id: string) {
    return updateProcess(id, {
      status: ProcessStatus.CANCELLED,
      end_date: new Date().toISOString()
    });
  }

  /**
   * Subscribe to real-time updates for forecasting processes
   */
  function subscribeToProcesses(callback: (payload: any) => void) {
    return supabase
      .channel('forecasting-process-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'forecasting_process' },
        callback
      )
      .subscribe();
  }

  return {
    processes,
    currentProcess,
    loading,
    error,
    activeProcesses,
    getActiveProcesses,
    fetchProcesses,
    fetchProcessById,
    createProcess,
    updateProcess,
    updateProcessStep,
    completeProcess,
    cancelProcess,
    subscribeToProcesses
  };
}

// The useTasks function has been moved to its own file: src/composables/useTasks.ts

// The useReminders function was removed as it was not used anywhere in the project
